// /app/frontend/src/pages/FeedPage.jsx
// Feed Social da Plataforma com Reações, Comentários e Posts Automáticos de Conquistas

import React, { useState, useEffect } from 'react';
import StoriesBar from '@/components/StoriesBar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Send, Loader2, ArrowLeft, MoreHorizontal,
  TrendingUp, Clock, Trash2, Users, Smile, MessageCircle,
  Trophy, Medal, PartyPopper, Star, Zap, Shield, Camera, X, Image as ImageIcon, Heart, Info
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { compressImage, isImagemValida, mensagemErroApi } from '@/lib/upload';
import FeedPostCard from '@/components/feed/FeedPostCard';
import FeedAdminSidebar from '@/components/feed/FeedAdminSidebar';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reações disponíveis
const REACOES = {
  aplausos: { emoji: "👏", nome: "Aplausos" },
  corrida: { emoji: "🏃", nome: "Correndo" },
  forca: { emoji: "💪", nome: "Força" },
  fogo: { emoji: "🔥", nome: "Em chamas" },
  coracao: { emoji: "❤️", nome: "Amei" },
  festa: { emoji: "🎉", nome: "Celebrando" },
  trofeu: { emoji: "🏆", nome: "Campeão" },
  parabens: { emoji: "🎊", nome: "Parabéns" }
};

// Tipos de posts automáticos
const TIPOS_POST = {
  texto: { label: "Texto", cor: "bg-slate-600" },
  foto: { label: "Foto", cor: "bg-teal-600", icone: Camera },
  conquista: { label: "Conquista", cor: "bg-purple-600", icone: Trophy },
  corrida_aprovada: { label: "Resultado", cor: "bg-green-600", icone: Medal },
  resultado: { label: "Resultado", cor: "bg-blue-600", icone: Zap }
};

const FeedPage = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Novo post
  const [novoPost, setNovoPost] = useState('');
  const [enviandoPost, setEnviandoPost] = useState(false);
  
  // Foto do post
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotosRestantes, setFotosRestantes] = useState(2);
  
  // Comentários
  const [comentarioTexto, setComentarioTexto] = useState({});
  const [enviandoComentario, setEnviandoComentario] = useState(null);
  const [showComentarios, setShowComentarios] = useState({});
  
  // Parabéns
  const [enviandoParabens, setEnviandoParabens] = useState(null);

  // Double-tap like
  const [heartAnimation, setHeartAnimation] = useState({});
  const lastTapRef = React.useRef({});

  useEffect(() => {
    if (authLoading) return;
    // Visitante: carregar feed sem redirecionar
    fetchFeed();
    fetchTrending();
    if (user) fetchFotosRestantes();
    const timeout = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(timeout);
  }, [user, authLoading]);

  const fetchFeed = async (pag = 1, append = false) => {
    if (pag === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/feed?pagina=${pag}&limite=10`, { headers });
      
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts || []);
      }
      
      setHasMore(pag < response.data.total_paginas);
      setPagina(pag);
    } catch (error) {
      console.error('Erro ao buscar feed:', error);
      toast.error('Erro ao carregar feed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchTrending = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/feed/trending?limite=5`, { headers });
      setTrending(response.data.trending || []);
    } catch (error) {
      console.error('Erro ao buscar trending:', error);
    }
  };

  const fetchFotosRestantes = async () => {
    try {
      const response = await axios.get(`${API}/feed/fotos-restantes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFotosRestantes(response.data.restantes);
    } catch (error) {
      console.error('Erro ao buscar fotos restantes:', error);
    }
  };

  const handleSelecionarFoto = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;

    if (!isImagemValida(original)) {
      toast.error('Formato não suportado. Use JPG, PNG ou WebP.');
      return;
    }
    const file = await compressImage(original);
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 5MB');
      return;
    }

    setFotoSelecionada(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoverFoto = () => {
    setFotoSelecionada(null);
    setFotoPreview(null);
  };

  const handleCriarPost = async () => {
    if (!novoPost.trim() && !fotoSelecionada) return;
    
    setEnviandoPost(true);
    try {
      if (fotoSelecionada) {
        // Post com foto
        const formData = new FormData();
        formData.append('foto', fotoSelecionada);
        formData.append('texto', novoPost);
        
        const response = await axios.post(
          `${API}/feed/posts/com-foto`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Foto publicada!');
        setFotosRestantes(response.data.fotos_restantes_hoje);
      } else {
        // Post só texto
        await axios.post(
          `${API}/feed/posts`,
          { texto: novoPost, tipo: 'texto' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post publicado!');
      }
      
      setNovoPost('');
      handleRemoverFoto();
      fetchFeed();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      
      if (error.response?.status === 429) {
        toast.error(typeof errorData === 'string' ? errorData : 'Limite de fotos diário atingido!');
      } else if (errorData && typeof errorData === 'object' && errorData.message) {
        const { message, nivel, educativo } = errorData;
        if (nivel === 'grave') {
          toast.error(message, { duration: 8000 });
          if (educativo) toast.warning(educativo, { duration: 10000 });
        } else if (nivel === 'medio') {
          toast.warning(message, { duration: 6000 });
          if (educativo) toast.info(educativo, { duration: 8000 });
        } else {
          toast.info(message, { duration: 5000 });
        }
      } else {
        toast.error(mensagemErroApi(error, 'Erro ao publicar'), { duration: 6000 });
      }
    } finally {
      setEnviandoPost(false);
    }
  };

  const handleDoubleTap = (postId) => {
    const now = Date.now();
    const lastTap = lastTapRef.current[postId] || 0;

    if (now - lastTap < 350) {
      // Double tap detected
      setHeartAnimation(prev => ({ ...prev, [postId]: true }));
      handleReagir(postId, 'coracao');
      setTimeout(() => {
        setHeartAnimation(prev => ({ ...prev, [postId]: false }));
      }, 1000);
      lastTapRef.current[postId] = 0;
    } else {
      lastTapRef.current[postId] = now;
    }
  };

  const requireLogin = () => {
    toast.info('Cadastre-se para interagir!', {
      action: { label: 'Cadastrar', onClick: () => navigate('/cadastro') }
    });
  };

  const handleReagir = async (postId, tipoReacao) => {
    if (!user) { requireLogin(); return; }
    try {
      const response = await axios.post(
        `${API}/feed/posts/${postId}/reagir`,
        { tipo_reacao: tipoReacao },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualizar o post localmente
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const novasReacoes = { ...post.reacoes };
          const minhaReacaoAnterior = post.minha_reacao;
          
          // Remover contagem da reação anterior se houver
          if (minhaReacaoAnterior && novasReacoes[minhaReacaoAnterior]) {
            novasReacoes[minhaReacaoAnterior] = {
              ...novasReacoes[minhaReacaoAnterior],
              count: novasReacoes[minhaReacaoAnterior].count - 1
            };
            if (novasReacoes[minhaReacaoAnterior].count <= 0) {
              delete novasReacoes[minhaReacaoAnterior];
            }
          }
          
          // Se removeu a reação
          if (response.data.removida) {
            return {
              ...post,
              reacoes: novasReacoes,
              minha_reacao: null,
              total_reacoes: Math.max(0, (post.total_reacoes || 1) - 1)
            };
          }
          
          // Adicionar nova reação
          if (!novasReacoes[tipoReacao]) {
            novasReacoes[tipoReacao] = {
              count: 0,
              emoji: REACOES[tipoReacao].emoji,
              nome: REACOES[tipoReacao].nome
            };
          }
          novasReacoes[tipoReacao] = {
            ...novasReacoes[tipoReacao],
            count: novasReacoes[tipoReacao].count + 1
          };
          
          const totalAnterior = post.total_reacoes || 0;
          const novoTotal = minhaReacaoAnterior ? totalAnterior : totalAnterior + 1;
          
          return {
            ...post,
            reacoes: novasReacoes,
            minha_reacao: tipoReacao,
            total_reacoes: novoTotal
          };
        }
        return post;
      }));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reagir');
    }
  };

  const handleComentar = async (postId) => {
    if (!user) { requireLogin(); return; }
    const texto = comentarioTexto[postId];
    if (!texto?.trim()) return;
    
    setEnviandoComentario(postId);
    try {
      const response = await axios.post(
        `${API}/feed/posts/${postId}/comentarios`,
        { texto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComentarioTexto({ ...comentarioTexto, [postId]: '' });
      toast.success('Comentário adicionado!');
      
      // Mostrar selo se ganhou
      if (response.data?.selo_respeitoso) {
        toast.success('🏅 Parabéns! Você ganhou o Selo de Atleta Respeitoso!', {
          duration: 5000
        });
      }
      
      // Recarregar feed para atualizar comentários
      fetchFeed();
    } catch (error) {
      console.log('Erro de moderação (debug):', error.response?.data);
      const errorData = error.response?.data?.detail;
      
      // Verificar se é erro de moderação (objeto com detalhes)
      if (errorData && typeof errorData === 'object' && errorData.message) {
        const { message, nivel, educativo } = errorData;
        
        // Mostrar mensagem baseada no nível
        if (nivel === 'grave') {
          toast.error(`🚫 ${message}`, { duration: 8000 });
          if (educativo) {
            setTimeout(() => {
              toast.warning(`💡 ${educativo}`, { duration: 10000 });
            }, 500);
          }
        } else if (nivel === 'medio') {
          toast.warning(`⚠️ ${message}`, { duration: 6000 });
          if (educativo) {
            setTimeout(() => {
              toast.info(`💡 ${educativo}`, { duration: 8000 });
            }, 500);
          }
        } else {
          toast.info(`ℹ️ ${message || 'Seu comentário precisa de revisão.'}`, { duration: 5000 });
        }
      } else {
        // Erro comum (string)
        toast.error(mensagemErroApi(error, 'Erro ao comentar'));
      }
    } finally {
      setEnviandoComentario(null);
    }
  };

  const handleDeletarPost = async (postId) => {
    try {
      await axios.delete(`${API}/feed/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deletado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar post');
    }
  };

  const handleParabens = async (postId) => {
    if (!user) { requireLogin(); return; }
    setEnviandoParabens(postId);
    try {
      await axios.post(
        `${API}/feed/posts/${postId}/parabens`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('🎊 Parabéns enviado!');
      
      // Atualizar o post localmente para mostrar a reação
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const novasReacoes = { ...post.reacoes };
          if (!novasReacoes.parabens) {
            novasReacoes.parabens = {
              count: 0,
              emoji: "🎊",
              nome: "Parabéns"
            };
          }
          novasReacoes.parabens.count += 1;
          
          return {
            ...post,
            reacoes: novasReacoes,
            minha_reacao: "parabens",
            total_reacoes: (post.total_reacoes || 0) + 1
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar parabéns');
    } finally {
      setEnviandoParabens(null);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr);
    const agora = new Date();
    const diff = agora - data;
    
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);
    
    if (minutos < 1) return 'Agora mesmo';
    if (minutos < 60) return `${minutos}m`;
    if (horas < 24) return `${horas}h`;
    if (dias < 7) return `${dias}d`;
    
    return data.toLocaleDateString('pt-BR');
  };

  // Funções de Admin para gerenciar comentários
  const handleFixarComentario = async (comentarioId) => {
    try {
      const response = await axios.post(
        `${API}/feed/admin/comentarios/${comentarioId}/fixar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchFeed(); // Recarregar posts para atualizar UI
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fixar comentário');
    }
  };

  const handleExcluirComentario = async (comentarioId, postId) => {
    if (!confirm('Tem certeza que deseja excluir este comentário?')) return;
    
    try {
      await axios.delete(
        `${API}/feed/admin/comentarios/${comentarioId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comentário excluído');
      
      // Atualizar localmente
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            comentarios_preview: post.comentarios_preview?.filter(c => c.id !== comentarioId),
            total_comentarios: Math.max(0, (post.total_comentarios || 0) - 1)
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir comentário');
    }
  };

  const handleBloquearUsuario = async (usuarioId, usuarioNome) => {
    const motivo = prompt(`Motivo para bloquear ${usuarioNome}:`, 'Violação das regras do feed');
    if (!motivo) return;
    
    try {
      await axios.post(
        `${API}/feed/admin/usuarios/bloquear`,
        { usuario_id: usuarioId, motivo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${usuarioNome} foi bloqueado de comentar no feed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao bloquear usuário');
    }
  };

  // Componente para exibir as reações de um post
  const ReacoesDisplay = ({ post }) => {
    const reacoes = post.reacoes || {};
    const reacoesArray = Object.entries(reacoes).filter(([_, r]) => r.count > 0);
    
    if (reacoesArray.length === 0) return null;
    
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {reacoesArray.map(([tipo, reacao]) => (
          <span 
            key={tipo} 
            className="flex items-center gap-0.5 bg-slate-700/50 rounded-full px-2 py-0.5 text-xs"
            title={`${reacao.count} ${reacao.nome}`}
          >
            <span className="text-sm">{reacao.emoji}</span>
            <span className="text-slate-300">{reacao.count}</span>
          </span>
        ))}
      </div>
    );
  };

  // Componente botão de reação
  const ReacaoButton = ({ post }) => {
    const [open, setOpen] = useState(false);
    const minhaReacao = post.minha_reacao;
    
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`${minhaReacao ? 'text-amber-400' : 'text-slate-400'} hover:text-amber-400 hover:bg-slate-700/50`}
            data-testid={`reaction-btn-${post.id}`}
          >
            {minhaReacao ? (
              <span className="text-lg mr-1">{REACOES[minhaReacao]?.emoji || "👏"}</span>
            ) : (
              <Smile className="w-4 h-4 mr-1" />
            )}
            {post.total_reacoes || 0}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2 bg-slate-800 border-slate-700" align="start">
          <div className="flex gap-1">
            {Object.entries(REACOES).map(([tipo, { emoji, nome }]) => (
              <Button
                key={tipo}
                variant="ghost"
                size="sm"
                className={`text-2xl hover:bg-slate-700 p-2 h-auto transition-transform hover:scale-125 ${minhaReacao === tipo ? 'bg-slate-700 ring-2 ring-amber-500' : ''}`}
                onClick={() => {
                  handleReagir(post.id, tipo);
                  setOpen(false);
                }}
                title={nome}
                data-testid={`reaction-${tipo}-${post.id}`}
              >
                {emoji}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Componente para exibir conteúdo especial de posts automáticos
  const PostConteudoEspecial = ({ post }) => {
    const tipo = post.tipo;
    const isAutoGerado = post.auto_gerado;
    
    // Post de conquista/insígnia
    if (tipo === 'conquista' && post.conquista_dados) {
      const { nome, descricao, emoji } = post.conquista_dados;
      return (
        <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/30">
              <span className="text-2xl">{emoji || "🏆"}</span>
            </div>
            <div>
              <Badge className="bg-purple-600 text-white mb-1">Nova Conquista!</Badge>
              <h4 className="font-bold text-white text-lg">{nome}</h4>
            </div>
          </div>
          {descricao && (
            <p className="text-purple-200 text-sm ml-15">{descricao}</p>
          )}
        </div>
      );
    }
    
    // Post de corrida aprovada
    if (tipo === 'corrida_aprovada' && post.resultado_dados) {
      const { nome_corrida, colocacao, pontos, distancia, tempo } = post.resultado_dados;
      const medalhas = { 1: "🥇", 2: "🥈", 3: "🥉" };
      const medalha = medalhas[colocacao] || "🏃";
      
      return (
        <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
              <span className="text-2xl">{medalha}</span>
            </div>
            <div>
              <Badge className="bg-green-600 text-white mb-1">Resultado Aprovado!</Badge>
              <h4 className="font-bold text-white text-lg">{nome_corrida}</h4>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 ml-15">
            {colocacao > 0 && (
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <p className="text-xs text-slate-400">Colocação</p>
                <p className="text-lg font-bold text-white">{colocacao}º</p>
              </div>
            )}
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <p className="text-xs text-slate-400">Pontos</p>
              <p className="text-lg font-bold text-amber-400">+{pontos}</p>
            </div>
            {distancia && (
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <p className="text-xs text-slate-400">Distância</p>
                <p className="text-lg font-bold text-white">{distancia}</p>
              </div>
            )}
            {tempo && (
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <p className="text-xs text-slate-400">Tempo</p>
                <p className="text-lg font-bold text-white">{tempo}</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Componente botão de Parabéns para posts especiais
  const BotaoParabens = ({ post }) => {
    const isConquistaOuResultado = post.tipo === 'conquista' || post.tipo === 'corrida_aprovada';
    const jaParabenizou = post.minha_reacao === 'parabens';
    
    if (!isConquistaOuResultado) return null;
    
    return (
      <Button
        variant={jaParabenizou ? "default" : "outline"}
        size="sm"
        onClick={() => handleParabens(post.id)}
        disabled={enviandoParabens === post.id || jaParabenizou}
        className={`${jaParabenizou 
          ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white border-0' 
          : 'border-purple-500/50 text-purple-400 hover:bg-purple-500/20'
        }`}
        data-testid={`parabens-btn-${post.id}`}
      >
        {enviandoParabens === post.id ? (
          <Loader2 className="w-4 h-4 animate-spin mr-1" />
        ) : (
          <PartyPopper className="w-4 h-4 mr-1" />
        )}
        {jaParabenizou ? 'Parabenizado!' : 'Parabéns!'}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="feed-page">
      <style>{`
        @keyframes heartBurst {
          0% { transform: scale(0); opacity: 0; }
          15% { transform: scale(1.3); opacity: 1; }
          30% { transform: scale(0.95); opacity: 1; }
          45% { transform: scale(1.1); opacity: 1; }
          80% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Feed Social</h1>
                <p className="text-xs text-slate-400">Compartilhe suas conquistas</p>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="ml-2 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center hover:bg-blue-500/30 transition-colors" data-testid="feed-info-btn">
                    <Info className="w-4 h-4 text-blue-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-slate-800 border-slate-700 text-slate-200 p-4" side="bottom" align="start">
                  <h4 className="font-semibold text-white text-sm mb-3">Como funciona o Feed</h4>
                  <div className="space-y-3 text-xs leading-relaxed">
                    <div className="flex gap-2">
                      <Clock className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      <p>Para manter a experiência leve e atual, as conversas, posts, fotos e arquivos são <strong className="text-white">redefinidos semanalmente aos domingos às 23h59</strong>.</p>
                    </div>
                    <div className="flex gap-2">
                      <Shield className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p>Para garantir a máxima <strong className="text-white">privacidade dos usuários</strong>, o sistema elimina automaticamente todos os dados armazenados semanalmente, evitando qualquer retenção desnecessária de informações sensíveis.</p>
                    </div>
                    <div className="flex gap-2">
                      <Zap className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <p>O reset semanal permite que os usuários <strong className="text-white">comecem cada ciclo social do zero</strong>, incentivando interações mais espontâneas e reduzindo o peso de conversas passadas.</p>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feed Principal */}
          <div className="lg:col-span-2 space-y-4">
            {/* Stories Bar */}
            <StoriesBar token={token} userId={user?.id} />

            {/* Criar Post - apenas para logados */}
            {user ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-amber-500 text-white">
                      {user?.nome?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-3">
                    <Textarea
                      placeholder={fotoSelecionada ? "Adicione uma legenda..." : "O que você quer compartilhar?"}
                      value={novoPost}
                      onChange={(e) => setNovoPost(e.target.value)}
                      className="bg-slate-700 border-slate-600 min-h-[80px] resize-none text-white font-semibold placeholder:text-slate-400 placeholder:font-normal"
                      maxLength={1000}
                      data-testid="new-post-input"
                    />

                    {/* Preview da foto selecionada */}
                    {fotoPreview && (
                      <div className="relative inline-block">
                        <img
                          src={fotoPreview}
                          alt="Preview"
                          className="rounded-lg max-h-60 object-cover border border-slate-600"
                          data-testid="foto-preview"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white h-7 w-7 rounded-full"
                          onClick={handleRemoverFoto}
                          data-testid="remover-foto-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Botão de foto */}
                        <input
                          type="file"
                          id="foto-input"
                          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                          className="hidden"
                          onChange={handleSelecionarFoto}
                          data-testid="foto-file-input"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`text-slate-400 hover:text-green-400 hover:bg-slate-700/50 ${fotosRestantes === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                          onClick={() => fotosRestantes > 0 && document.getElementById('foto-input').click()}
                          disabled={fotosRestantes === 0 || !!fotoSelecionada}
                          data-testid="add-foto-btn"
                        >
                          <Camera className="w-5 h-5 mr-1" />
                          Foto
                        </Button>
                        <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="fotos-restantes-label">
                          {fotoSelecionada
                            ? `${fotosRestantes} foto${fotosRestantes !== 1 ? 's' : ''} restante${fotosRestantes !== 1 ? 's' : ''} hoje`
                            : `${fotosRestantes}/${2} fotos hoje`
                          }
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="hidden sm:inline text-xs text-slate-400 whitespace-nowrap">
                          {novoPost.length}/1000
                        </span>
                        <Button
                          onClick={handleCriarPost}
                          disabled={(!novoPost.trim() && !fotoSelecionada) || enviandoPost}
                          className="bg-amber-500 hover:bg-amber-600 text-black"
                          data-testid="publish-post-btn"
                        >
                          {enviandoPost ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Publicar
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            ) : (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-300 text-sm">Quer publicar, comentar ou reagir?</p>
                  </div>
                  <Button onClick={() => navigate('/cadastro')} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">
                    Cadastre-se
                  </Button>
                  <Button onClick={() => navigate('/login')} variant="outline" size="sm" className="border-slate-600 text-slate-300">
                    Entrar
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Lista de Posts */}
            {posts.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Feed vazio</h3>
                  <p className="text-slate-400">Seja o primeiro a compartilhar algo!</p>
                </CardContent>
              </Card>
            ) : (
              posts.map((post) => (
                <FeedPostCard
                  key={post.id}
                  post={post}
                  user={user}
                  isAdmin={isAdmin}
                  token={token}
                  ReacoesDisplay={ReacoesDisplay}
                  ReacaoButton={ReacaoButton}
                  PostConteudoEspecial={PostConteudoEspecial}
                  BotaoParabens={BotaoParabens}
                  handleDeletarPost={handleDeletarPost}
                  handleDoubleTap={handleDoubleTap}
                  handleComentar={handleComentar}
                  handleFixarComentario={handleFixarComentario}
                  handleExcluirComentario={handleExcluirComentario}
                  handleBloquearUsuario={handleBloquearUsuario}
                  heartAnimation={heartAnimation}
                  showComentarios={showComentarios}
                  setShowComentarios={setShowComentarios}
                  comentarioTexto={comentarioTexto}
                  setComentarioTexto={setComentarioTexto}
                  enviandoComentario={enviandoComentario}
                  formatarData={formatarData}
                />
              ))
            )}
            
            {/* Carregar Mais */}
            {hasMore && (
              <div className="text-center py-4">
                <Button
                  variant="outline"
                  onClick={() => fetchFeed(pagina + 1, true)}
                  disabled={loadingMore}
                  className="border-slate-600"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Carregar mais
                </Button>
              </div>
            )}
          </div>

          {/* Sidebar - Extraído */}
          <FeedAdminSidebar isAdmin={isAdmin} token={token} fetchFeed={fetchFeed} />
        </div>
      </div>
    </div>
  );
};

export default FeedPage;
